// admin-panel/src/pages/Inquiries.jsx
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { inquiriesApi } from '../lib/api';
import { StatusBadge } from './Dashboard';
import { Search, Filter, MessageCircle, Eye, Trash2, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';

const STATUSES  = ['', 'new', 'contacted', 'in_progress', 'quoted', 'converted', 'closed', 'spam'];
const PRIORITIES = ['', 'low', 'medium', 'high', 'urgent'];
const SOURCES   = ['', 'website', 'whatsapp', 'referral', 'walk_in'];

export default function Inquiries() {
  const qc = useQueryClient();
  const [filters, setFilters] = useState({ status: '', search: '', priority: '', source: '', page: 1 });
  const [showFilters, setShowFilters] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['inquiries', filters],
    queryFn: () => inquiriesApi.list({ ...filters, per_page: 20 }),
  });

  const { data: stats } = useQuery({ queryKey: ['inquiry-stats'], queryFn: inquiriesApi.stats });

  const deleteMut = useMutation({
    mutationFn: inquiriesApi.delete,
    onSuccess: () => { qc.invalidateQueries(['inquiries']); toast.success('Inquiry deleted'); },
  });

  const updateMut = useMutation({
    mutationFn: ({ id, data }) => inquiriesApi.update(id, data),
    onSuccess: () => { qc.invalidateQueries(['inquiries']); toast.success('Status updated'); },
  });

  const whatsappMut = useMutation({
    mutationFn: ({ id }) => inquiriesApi.sendWhatsApp(id, {}),
    onSuccess: (res) => { window.open(res.wa_url, '_blank'); toast.success('WhatsApp opened'); },
  });

  function setFilter(key, val) {
    setFilters(f => ({ ...f, [key]: val, page: 1 }));
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white">Inquiries</h1>
          <p className="text-sm text-[#555] mt-0.5">Manage customer inquiries and leads</p>
        </div>
        <button onClick={() => refetch()} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-[#888] hover:text-white transition-all">
          <RefreshCw size={16} />
        </button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        {[
          { label: 'Total', val: stats?.total, color: '#888' },
          { label: 'New', val: stats?.new, color: '#D4AF37' },
          { label: 'Unread', val: stats?.unread, color: '#ef4444' },
          { label: 'In Progress', val: stats?.in_progress, color: '#a78bfa' },
          { label: 'Quoted', val: stats?.quoted, color: '#f97316' },
          { label: 'Converted', val: stats?.converted, color: '#22c55e' },
        ].map(({ label, val, color }) => (
          <button key={label} onClick={() => setFilter('status', label === 'Total' ? '' : label.toLowerCase().replace(' ', '_'))}
            className="bg-[#151515] border border-white/5 hover:border-white/10 rounded-xl p-3 text-left transition-all">
            <div className="text-xl font-bold" style={{ color }}>{val ?? '—'}</div>
            <div className="text-xs text-[#555] mt-0.5">{label}</div>
          </button>
        ))}
      </div>

      {/* Search + Filters */}
      <div className="flex gap-3">
        <div className="relative flex-1 max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
          <input value={filters.search} onChange={e => setFilter('search', e.target.value)}
            placeholder="Search name, phone, email..."
            className="w-full bg-[#151515] border border-white/5 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/30" />
        </div>
        <button onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm border transition-all ${showFilters ? 'bg-[#D4AF37]/10 border-[#D4AF37]/20 text-[#D4AF37]' : 'bg-[#151515] border-white/5 text-[#888]'}`}>
          <Filter size={15} /> Filters
        </button>
      </div>

      {showFilters && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 bg-[#151515] rounded-xl border border-white/5">
          {[
            { label: 'Status', key: 'status', opts: STATUSES },
            { label: 'Priority', key: 'priority', opts: PRIORITIES },
            { label: 'Source', key: 'source', opts: SOURCES },
          ].map(({ label, key, opts }) => (
            <div key={key}>
              <label className="block text-xs text-[#555] mb-1.5">{label}</label>
              <select value={filters[key]} onChange={e => setFilter(key, e.target.value)}
                className="w-full bg-[#1A1A1A] border border-white/5 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#D4AF37]/30">
                <option value="">All</option>
                {opts.filter(Boolean).map(o => <option key={o} value={o} className="capitalize">{o.replace('_', ' ')}</option>)}
              </select>
            </div>
          ))}
          <div className="flex items-end">
            <button onClick={() => setFilters({ status: '', search: '', priority: '', source: '', page: 1 })}
              className="w-full py-2 text-sm text-[#888] hover:text-white bg-white/5 hover:bg-white/10 rounded-lg transition-all">
              Reset
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-[#151515] border border-white/5 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/5">
                {['Name', 'Contact', 'Service', 'Status', 'Priority', 'Date', 'Actions'].map(h => (
                  <th key={h} className="text-left text-xs text-[#555] font-medium px-4 py-3 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array(8).fill(0).map((_, i) => (
                  <tr key={i} className="border-b border-white/5">
                    {Array(7).fill(0).map((_, j) => (
                      <td key={j} className="px-4 py-3"><div className="h-4 bg-white/5 rounded animate-pulse w-20" /></td>
                    ))}
                  </tr>
                ))
              ) : data?.data?.map((inq) => (
                <tr key={inq.id} className={`border-b border-white/5 hover:bg-white/3 transition-colors ${!inq.is_read ? 'bg-[#D4AF37]/3' : ''}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {!inq.is_read && <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] shrink-0" />}
                      <div>
                        <Link to={`/inquiries/${inq.id}`} className="text-white hover:text-[#D4AF37] font-medium transition-colors">
                          {inq.name}
                        </Link>
                        {inq.company && <div className="text-xs text-[#555]">{inq.company}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-[#888]">{inq.phone}</div>
                    {inq.email && <div className="text-xs text-[#555]">{inq.email}</div>}
                  </td>
                  <td className="px-4 py-3 text-[#888] text-xs">{inq.service?.title || '—'}</td>
                  <td className="px-4 py-3">
                    <select value={inq.status}
                      onChange={e => updateMut.mutate({ id: inq.id, data: { status: e.target.value } })}
                      className="bg-transparent text-xs border-0 focus:outline-none cursor-pointer">
                      {STATUSES.filter(Boolean).map(s => (
                        <option key={s} value={s} className="bg-[#1A1A1A] capitalize">{s.replace('_', ' ')}</option>
                      ))}
                    </select>
                    <StatusBadge status={inq.status} />
                  </td>
                  <td className="px-4 py-3">
                    <PriorityBadge priority={inq.priority} />
                  </td>
                  <td className="px-4 py-3 text-[#555] text-xs whitespace-nowrap">
                    {new Date(inq.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <Link to={`/inquiries/${inq.id}`}
                        className="p-1.5 rounded-lg hover:bg-white/10 text-[#888] hover:text-white transition-all">
                        <Eye size={14} />
                      </Link>
                      <button onClick={() => whatsappMut.mutate({ id: inq.id })}
                        className="p-1.5 rounded-lg hover:bg-green-500/10 text-[#888] hover:text-green-400 transition-all">
                        <MessageCircle size={14} />
                      </button>
                      <button onClick={() => { if(window.confirm('Delete this inquiry?')) deleteMut.mutate(inq.id) }}  className="p-1.5 rounded-lg hover:bg-red-500/10 text-[#888] hover:text-red-400 transition-all">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {data && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-white/5">
            <div className="text-xs text-[#555]">
              Showing {((filters.page - 1) * 20) + 1}–{Math.min(filters.page * 20, data.total)} of {data.total}
            </div>
            <div className="flex items-center gap-2">
              <button disabled={filters.page <= 1} onClick={() => setFilter('page', filters.page - 1)}
                className="p-1.5 rounded-lg hover:bg-white/10 text-[#888] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all">
                <ChevronLeft size={16} />
              </button>
              <span className="text-xs text-[#888]">Page {filters.page} / {data.last_page}</span>
              <button disabled={filters.page >= data.last_page} onClick={() => setFilter('page', filters.page + 1)}
                className="p-1.5 rounded-lg hover:bg-white/10 text-[#888] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all">
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PriorityBadge({ priority }) {
  const map = {
    low: 'text-gray-400', medium: 'text-blue-400', high: 'text-orange-400', urgent: 'text-red-400'
  };
  return <span className={`text-xs font-medium capitalize ${map[priority] || 'text-gray-400'}`}>{priority}</span>;
}
